//* 관리자: 같은 지사 지도사 계정 일괄 발급
//* 소속(지역·팀타입·팀이름·테마·답례파일럿)은 한 번만 고르고, 계정별로 아이디·비밀번호·이름·전화번호만 받는다.
//* 엑셀에서 복사해 표 아무 칸에 붙여넣으면 여러 줄이 한 번에 채워진다.
//* 백엔드 일괄 API 없이 기존 admin/user 생성을 행마다 순차 호출 — 실패 행만 남겨 재시도한다.
import { ClipboardEvent, FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { GiftPilotStoreWarning } from '@service/admin/components/GiftPilotStoreWarning';
import { bugoBrandListHookUrl } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrand, BugoBrandData } from '@shared/api/bugoBrand/bugoBrand.interface';
import { adminCreateUser, adminFindTakenUsernames } from '@shared/api/user/user.controller';
import { Button } from '@shared/components/Button';
import { Label } from '@shared/components/Label';
import { LoadingModal } from '@shared/components/LoadingModal';
import Select from '@shared/containers/Select/Select';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { UserCustomTheme } from '@shared/types';
import { isGiftBrandLive } from '@shared/utils/giftPilot.util';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import {
  BugoBrandAgencyWorkerFormInputs,
  BugoBrandAgencyWorkerFormInputsEnum,
} from '../AdminBugoBrandAgencyWorkerForm/AdminBugoBrandAgencyWorkerForm.interface';
import { buildAgencyWorkerCreateDto } from '../AdminBugoBrandAgencyWorkerForm/AdminBugoBrandAgencyWorkerForm.util';
import {
  applyGridPaste,
  BulkWorkerColumn,
  BulkWorkerRow,
  bulkWorkerCreateErrorMessage,
  isBlankRow,
  isGridPaste,
  makeBulkWorkerRow,
  onlyDigits,
  validateBulkWorkerRows,
} from './AdminBugoBrandAgencyWorkerBulkForm.util';

type SharedInputs = Pick<
  BugoBrandAgencyWorkerFormInputs,
  | BugoBrandAgencyWorkerFormInputsEnum.Theme
  | BugoBrandAgencyWorkerFormInputsEnum.Region
  | BugoBrandAgencyWorkerFormInputsEnum.TeamType
  | BugoBrandAgencyWorkerFormInputsEnum.TeamName
  | BugoBrandAgencyWorkerFormInputsEnum.GiftPilot
>;

const COLUMNS: { field: BulkWorkerColumn; label: string }[] = [
  { field: 'username', label: '아이디' },
  { field: 'password', label: '비밀번호' },
  { field: 'name', label: '이름' },
  { field: 'phoneNumber', label: '휴대전화번호' },
];

const INITIAL_ROW_COUNT = 5;
const initialRows = () =>
  Array.from({ length: INITIAL_ROW_COUNT }, () => makeBulkWorkerRow());

const inputClass =
  'w-full rounded border px-2 py-1.5 text-14 theme-bg-1 disabled:opacity-60';

//* 아이디 입력이 멈춘 뒤 전체 계정 대조
const USERNAME_CHECK_DELAY = 400;

interface Props {
  bugoBrand: BugoBrand | BugoBrandData;
}

export const AdminBugoBrandAgencyWorkerBulkForm: FC<Props> =
  function AdminBugoBrandAgencyWorkerBulkForm({ bugoBrand }: Props) {
    const notificationInstance = useRecoilValue(notificationInstanceAtom);
    const queryClient = useQueryClient();

    const { control, getValues } = useForm<SharedInputs>({
      defaultValues: {
        theme: bugoBrand.bugoAgency?.theme as UserCustomTheme,
        giftPilot: false,
      },
    });

    const regionList = bugoBrand.bugoAgency?.regions ?? [''];
    const teamTypeList = bugoBrand.bugoAgency?.teamTypes ?? [''];
    const teamNameList = bugoBrand.bugoAgency?.teamNames ?? [''];

    const workers = useMemo(
      () => (bugoBrand as BugoBrandData).workers ?? [],
      [bugoBrand],
    );

    const [rows, setRows] = useState<BulkWorkerRow[]>(initialRows);
    const [showErrors, setShowErrors] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(
      null,
    );

    //* 서버 전체 계정 대조 결과 캐시 (아이디 → 이미 사용 중 여부)
    const [usernameTaken, setUsernameTaken] = useState<Record<string, boolean>>({});
    const takenUsernames = useMemo(
      () =>
        new Set(Object.keys(usernameTaken).filter((name) => usernameTaken[name] === true)),
      [usernameTaken],
    );

    const filledRows = useMemo(() => rows.filter((row) => !isBlankRow(row)), [rows]);
    const rowErrors = useMemo(
      () => validateBulkWorkerRows(rows, workers, takenUsernames),
      [rows, workers, takenUsernames],
    );
    const pendingRows = filledRows.filter((row) => row.status !== 'done');

    //* 아직 대조 안 한 아이디 — 입력이 멈추면 한 번에 조회
    const uncheckedKey = useMemo(
      () =>
        Array.from(
          new Set(
            pendingRows
              .map((row) => row.username)
              .filter((name) => name && usernameTaken[name] === undefined),
          ),
        )
          .sort()
          .join('\n'),
      [pendingRows, usernameTaken],
    );
    const [usernameChecking, setUsernameChecking] = useState(false);
    useEffect(() => {
      if (!uncheckedKey) return;
      let cancelled = false;
      const timer = setTimeout(async () => {
        const names = uncheckedKey.split('\n');
        setUsernameChecking(true);
        try {
          const taken = new Set(await adminFindTakenUsernames(names));
          if (cancelled) return;
          setUsernameTaken((prev) => {
            const next = { ...prev };
            names.forEach((name) => {
              next[name] = taken.has(name);
            });
            return next;
          });
        } catch (err) {
          //* 조회 실패는 [발급] 때 다시 확인한다
          console.error(err);
        } finally {
          if (!cancelled) setUsernameChecking(false);
        }
      }, USERNAME_CHECK_DELAY);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }, [uncheckedKey]);
    const doneCount = filledRows.length - pendingRows.length;
    const running = progress !== null;

    const updateRow = (key: string, patch: Partial<BulkWorkerRow>) => {
      setRows((prev) =>
        prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
      );
    };

    const onFieldChange = (key: string, field: BulkWorkerColumn, value: string) => {
      //* 입력을 고치면 이전 서버 실패 표시는 지운다
      updateRow(key, {
        [field]: field === 'phoneNumber' ? onlyDigits(value).slice(0, 11) : value.trim(),
        status: 'idle',
        error: undefined,
      });
    };

    //* 엑셀 여러 칸/여러 줄 붙여넣기 → 표 채우기 (한 칸짜리는 기본 동작)
    const onCellPaste = (
      e: ClipboardEvent<HTMLInputElement>,
      rowIndex: number,
      field: BulkWorkerColumn,
    ) => {
      const text = e.clipboardData.getData('text');
      if (!isGridPaste(text)) return;
      e.preventDefault();
      const result = applyGridPaste(rows, rowIndex, field, text);
      setRows(result.rows);
      notificateSuccess(notificationInstance, `${result.filled}줄 붙여넣기`);
    };

    //* 발급 버튼 잠금(progress)은 아이디 대조가 끝난 뒤에야 걸린다 — 그 사이 연타하면 같은 계정이
    //* 두 번 생성되므로(서버는 아이디 중복을 거부하지 않음) 진입 즉시 막는다.
    const submittingRef = useRef(false);
    const onSubmit = async () => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        await submitRows();
      } finally {
        submittingRef.current = false;
      }
    };

    const submitRows = async () => {
      if (pendingRows.length === 0) {
        notificateError(notificationInstance, '발급할 계정을 입력해주세요');
        return;
      }
      //* 발급 직전엔 캐시와 상관없이 전체 계정과 다시 대조 (그 사이 다른 곳에서 만든 계정 대비)
      let freshTaken: Set<string>;
      try {
        freshTaken = new Set(
          await adminFindTakenUsernames(pendingRows.map((row) => row.username)),
        );
      } catch (err) {
        console.error(err);
        notificateError(notificationInstance, '아이디 중복 확인 실패 — 다시 시도해주세요');
        return;
      }
      setUsernameTaken((prev) => {
        const next = { ...prev };
        pendingRows.forEach((row) => {
          if (row.username) next[row.username] = freshTaken.has(row.username);
        });
        return next;
      });
      const submitErrors = validateBulkWorkerRows(rows, workers, freshTaken);
      if (pendingRows.some((row) => submitErrors[row.key])) {
        setShowErrors(true);
        notificateError(notificationInstance, '입력 오류가 있는 행을 확인해주세요');
        return;
      }

      const shared = getValues();
      const targets = pendingRows;
      let successCount = 0;
      setProgress({ done: 0, total: targets.length });
      for (let idx = 0; idx < targets.length; idx += 1) {
        const row = targets[idx];
        if (!row) continue;
        updateRow(row.key, { status: 'pending', error: undefined });
        try {
          await adminCreateUser(
            buildAgencyWorkerCreateDto(
              {
                ...shared,
                username: row.username,
                password: row.password,
                name: row.name,
                phoneNumber: row.phoneNumber,
              },
              bugoBrand,
            ),
          );
          successCount += 1;
          updateRow(row.key, { status: 'done' });
          setUsernameTaken((prev) => ({ ...prev, [row.username]: true }));
        } catch (err) {
          console.error(err);
          updateRow(row.key, {
            status: 'error',
            error: bulkWorkerCreateErrorMessage(err),
          });
        }
        setProgress({ done: idx + 1, total: targets.length });
      }
      setProgress(null);
      await queryClient.invalidateQueries(bugoBrandListHookUrl);

      const failCount = targets.length - successCount;
      if (failCount === 0) {
        notificateSuccess(notificationInstance, `${successCount}건 발급 완료`);
      } else {
        notificateError(
          notificationInstance,
          `${successCount}건 발급 · ${failCount}건 실패 — 실패 행 확인 후 다시 발급`,
        );
      }
    };

    const visibleError = (row: BulkWorkerRow) => {
      if (row.status !== 'idle') return undefined;
      const rowError = rowErrors[row.key];
      if (!rowError || (!rowError.live && !showErrors)) return undefined;
      return rowError;
    };

    const statusCell = (row: BulkWorkerRow) => {
      if (row.status === 'done') return <span className="text-green-600">발급완료</span>;
      if (row.status === 'pending') return <span className="theme-text-6">발급중</span>;
      if (row.status === 'error')
        return <span className="text-red-500">{row.error ?? '발급 실패'}</span>;
      const rowError = visibleError(row);
      if (rowError) return <span className="text-red-500">{rowError.message}</span>;
      if (usernameChecking && row.username && usernameTaken[row.username] === undefined)
        return <span className="theme-text-6">아이디 확인중</span>;
      return null;
    };

    return (
      <>
        <div className="space-y-5">
          <div>
            <p className="text-16 font-bold">지도사 계정 일괄 발급</p>
            <p className="text-12 theme-text-6">
              {bugoBrand.bugoAgency?.corpName} · 소속은 전원 공통 적용
            </p>
          </div>

          {/* 소속 공통값 */}
          <div className="space-y-2 rounded-md border p-3 theme-border-1">
            <p className="text-14 font-bold">소속 (공통)</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="label-col">
                <Label>지역</Label>
                <Select
                  placeholder="지역"
                  optionList={regionList}
                  control={control}
                  name={BugoBrandAgencyWorkerFormInputsEnum.Region}
                ></Select>
              </div>
              <div className="label-col">
                <Label>팀타입</Label>
                <Select
                  placeholder="팀타입"
                  optionList={teamTypeList}
                  control={control}
                  name={BugoBrandAgencyWorkerFormInputsEnum.TeamType}
                ></Select>
              </div>
              <div className="label-col">
                <Label>팀이름</Label>
                <Select
                  placeholder="팀이름"
                  optionList={teamNameList}
                  control={control}
                  name={BugoBrandAgencyWorkerFormInputsEnum.TeamName}
                ></Select>
              </div>
              <div className="label-col">
                <Label>테마</Label>
                <Select
                  placeholder="테마"
                  optionList={[
                    { value: 'Default', label: '기본' },
                    { value: 'Preed', label: '프리드' },
                    { value: 'Dark', label: '다크' },
                    { value: 'Daemyung', label: '대명' },
                  ]}
                  control={control}
                  name={BugoBrandAgencyWorkerFormInputsEnum.Theme}
                ></Select>
              </div>
              <div className="label-col">
                <Label>답례파일럿</Label>
                {/* 상조 전체 실행 중이면 파일럿은 의미 없음 — 기본값(미사용)으로 발급 */}
                {isGiftBrandLive(bugoBrand) ? (
                  <p className="py-2 text-14 text-gray-500">상조 전체 적용 중</p>
                ) : (
                  <Select
                    placeholder="답례파일럿"
                    optionList={[
                      { value: true, label: '사용' },
                      { value: false, label: '미사용' },
                    ]}
                    control={control}
                    name={BugoBrandAgencyWorkerFormInputsEnum.GiftPilot}
                  ></Select>
                )}
                <GiftPilotStoreWarning bugoBrand={bugoBrand} className="pt-1" />
              </div>
            </div>
          </div>

          {/* 계정 목록 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="text-14 font-bold">
                계정 {filledRows.length}건
                {doneCount > 0 && (
                  <span className="ml-1 font-normal text-green-600">
                    (발급완료 {doneCount})
                  </span>
                )}
              </p>
              <p className="text-12 theme-text-6">
                엑셀에서 아이디·비밀번호·이름·전화번호 순으로 복사 → 첫 칸에 붙여넣기
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-14">
                <thead>
                  <tr className="whitespace-nowrap text-12 theme-text-6">
                    <th className="w-8 px-1 py-1">#</th>
                    {COLUMNS.map((col) => (
                      <th key={col.field} className="px-1 py-1">
                        {col.label}
                      </th>
                    ))}
                    <th className="px-1 py-1">상태</th>
                    <th className="w-8 px-1 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const locked = running || row.status === 'done';
                    return (
                      <tr key={row.key} className="align-middle">
                        <td className="px-1 py-1 text-12 theme-text-6">{idx + 1}</td>
                        {COLUMNS.map((col) => (
                          <td key={col.field} className="px-1 py-1">
                            <input
                              className={`${inputClass} ${
                                visibleError(row)?.field === col.field
                                  ? 'border-red-500'
                                  : 'theme-border-1'
                              }`}
                              type={col.field === 'phoneNumber' ? 'tel' : 'text'}
                              value={
                                col.field === 'phoneNumber'
                                  ? phoneNumberStringFormator(row.phoneNumber)
                                  : row[col.field]
                              }
                              placeholder={col.label}
                              disabled={locked}
                              onPaste={(e) => onCellPaste(e, idx, col.field)}
                              onChange={(e) =>
                                onFieldChange(row.key, col.field, e.target.value)
                              }
                            />
                          </td>
                        ))}
                        <td className="max-w-[160px] px-1 py-1 text-12">
                          {statusCell(row)}
                        </td>
                        <td className="px-1 py-1 text-center">
                          {row.status !== 'done' && (
                            <button
                              type="button"
                              className="text-16 theme-text-6 disabled:opacity-40"
                              disabled={running}
                              aria-label="행 삭제"
                              onClick={() =>
                                setRows((prev) => prev.filter((r) => r.key !== row.key))
                              }
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                className="text-12 shadow-sm theme-bg-1"
                disabled={running}
                onClick={() => setRows((prev) => [...prev, makeBulkWorkerRow()])}
              >
                + 행 추가
              </Button>
              {doneCount > 0 && (
                <Button
                  type="button"
                  className="text-12 shadow-sm theme-bg-1"
                  disabled={running}
                  onClick={() => {
                    setRows(initialRows());
                    setShowErrors(false);
                  }}
                >
                  새로 입력
                </Button>
              )}
            </div>
          </div>

          <Button
            type="button"
            disabled={running || pendingRows.length === 0}
            className="button-rectangle min-h-[40px] rounded-md theme-text-3 theme-bg-main"
            onClick={onSubmit}
          >
            {pendingRows.length > 0 ? `${pendingRows.length}건 발급` : '발급'}
          </Button>
        </div>
        <LoadingModal
          open={running}
          title={progress ? `발급중 ${progress.done}/${progress.total}` : '발급중'}
        />
      </>
    );
  };
